import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import orderBy from 'lodash/orderBy'
import { useRouter } from 'next/router'
import { z } from 'zod'
import { Icon, Image, Input, Loader, Modal, Text, Tooltip } from '@/client/components'
import { CartContext } from '@/client/context/cart'
import { useDebounce, useGetCartItems, useGetCountries, useGetUserInfos } from '@/client/hooks'
import { useGetPrices, useMutatePrices } from '@/client/hooks/prices'
import {
  DEFAULT_CURRENCY,
  format,
  getAlternativeCurrency,
  getCurrencyCodeById,
  getCurrencyId,
  getEffectiveCurrency,
  setPreferredCurrency
} from '@/client/utils'
import { useI18n } from '@/locales'
import * as S from './styles'
import { isDateReal } from '../helper'
import { ResultFooter } from '../ResultFooter'
import { ARTWORKSERVICE_SELECTION, ResultProps, schema } from '../types'

export const Result: FC<ResultProps> = ({ result, texts, error_messages, compactView = false }) => {
  const { locale, defaultLocale } = useRouter()
  const { isLoading, mutate } = useMutatePrices(locale)
  const { updateSelection, selection } = useContext(CartContext)
  const alternativeCurrency = getAlternativeCurrency(defaultLocale)
  const { data: cartData } = useGetCartItems()
  const currentCurrency = getCurrencyCodeById(selection?.currency_id as number) || getEffectiveCurrency(cartData)
  // `selection._id` is reused across several unrelated flows (reorder from a
  // past job, redirect from an error email, ...), so its mere presence isn't
  // proof the user is editing an item that's actually in the cart right now.
  // Only allow the currency switch when the cart contains exactly that one
  // item - if there are other items too, changing currency would leave them
  // mismatched, so it stays locked.
  const isOnlyCartItemBeingEdited =
    !!selection?._id && cartData?.cart_items?.length === 1 && cartData.cart_items[0]._id === selection._id
  const hasCartItems = !!cartData?.cart_items?.length && !isOnlyCartItemBeingEdited
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { data } = useGetPrices(locale)
  // Show prices in the currency the price API actually calculated them in, so
  // the symbol always matches the amounts. Fall back to the selected currency
  // until a response carrying it has arrived.
  const priceCurrency = data?.jobProps?.currency || currentCurrency
  const { mutate: updatePrices } = useMutatePrices(locale)
  const [isClosing, setIsClosing] = useState<boolean>(false)
  const { data: userInfos } = useGetUserInfos()
  const [custName, setCustName] = useState<string>('')
  const [custEmail, setCustEmail] = useState<string>('')
  const debouncedValue = useDebounce(custName, 500)
  const debouncedValueEmail = useDebounce(custEmail, 500)
  const [countryCode, setCountryCode] = useState<string>('')
  const { data: countriesData } = useGetCountries(locale)
  const [defaultCountry, setDefaultCountry] = useState<number>(0)
  const t = useI18n()

  const noArtworkServiceId =
    data?.optionFields
      .find((item) => item.key === 'artwork_service')
      ?.options.find((optionItem) => optionItem.key === 'no_artwork_service')?.id ||
    ARTWORKSERVICE_SELECTION.NO_ARTWORK_SERVICE

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_GEOLOGICAL_COUNTRY}`)
      .then((response) => response.json())
      .then((data) => {
        setCountryCode(data.country_code)
      })
  }, [])

  useEffect(() => {
    if (!countriesData?.length) return
    const matchedCountry = countriesData.find((country) => country.landCode === countryCode)
    const countryId = matchedCountry?.id ?? countriesData[0].id
    setDefaultCountry(countryId)
  }, [countriesData, countryCode])

  const selectQuantity = useCallback(
    (quantity) => {
      const newSelection = { ...selection, quantity }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  const selectDeliveryType = useCallback(
    (type) => {
      const newSelection = { ...selection, super_express_production: false, express_production: type }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  const selectSuperExpressDeliveryType = useCallback(
    (type) => {
      const newSelection = { ...selection, express_production: false, super_express_production: type }
      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  useEffect(() => {
    if (!isDateReal(result.dateExpress)) {
      selectDeliveryType(false)
    }
  }, [result.dateExpress])

  const toggleCurrency = useCallback(() => {
    if (!alternativeCurrency || hasCartItems) return

    const nextCurrency = priceCurrency === DEFAULT_CURRENCY ? alternativeCurrency : DEFAULT_CURRENCY
    const newSelection = { ...selection }

    // Only carry a currency_id on the payload when it differs from the shop
    // default, keeping requests backward compatible with APIs that don't
    // support a currency field.
    if (nextCurrency === DEFAULT_CURRENCY) {
      delete newSelection.currency_id
    } else {
      newSelection.currency_id = getCurrencyId(nextCurrency)
    }

    // Persist the choice so it keeps being used for new calculations
    // (new sessions, resets, ...) until the user switches back.
    setPreferredCurrency(nextCurrency)

    updateSelection(newSelection)
    mutate(newSelection)
  }, [alternativeCurrency, hasCartItems, priceCurrency, selection, updateSelection, mutate])

  const handleSubmitForm = useCallback(() => {
    const newSelection = { ...selection }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { tpy_cta, tpy_lang, tpy_type, tpy_layout, ...rest } = selection

    newSelection.quotation_options = {
      mail_language: locale,
      shop_id: defaultLocale,
      save_quotation: 1,
      express_delivery: 0,
      super_express_delivery: 0,
      cust_name: debouncedValue,
      cust_mail: debouncedValueEmail,
      cust_id: userInfos?.customer_id ?? 0,
      delivery_country: userInfos?.country_id ?? defaultCountry,
      core_diameter: 0,
      roll_max_labels: 0,
      shipment: 0,
      discount_abs: 0,
      discount_percent: 0,
      surcharge: 0,
      proof_free: 0,
      proof_type: 0,
      artwork_service_free: 0,
      artwork_service_type: 0,
      calculator_selection: {
        //essential for making the quotations editable
        language: locale,
        shop: defaultLocale,
        ...rest
      }
    }

    updatePrices(newSelection)
    setIsModalOpen(false)

    setIsClosing(true)

    setTimeout(() => {
      setIsModalOpen(false)
      setIsClosing(false)
    }, 1200)
  }, [updatePrices, selection, debouncedValueEmail, debouncedValue])

  const handleValidateForm = useCallback(() => {
    const formSchema = schema(error_messages)

    try {
      formSchema.parse({
        quotation_cust_name: debouncedValue,
        quotation_cust_mail: debouncedValueEmail
      })

      return false
    } catch (e) {
      const errors = {}
      if (e instanceof z.ZodError) {
        e.errors.forEach((error) => {
          errors[error.path[0]] = error.message
        })
      }

      return true
    }
  }, [debouncedValue, debouncedValueEmail])

  const getPriceByQuantity = useCallback((): string => {
    const newPrice = result?.priceTable?.find((item) => item.quantity === Number(selection?.quantity))
    return !!newPrice?.totalPrice ? format.currency(newPrice?.totalPrice, priceCurrency, locale) : '--  €'
  }, [result?.priceTable, selection?.quantity, priceCurrency, locale])

  const getUnityPrice = useCallback(() => {
    const unityPrice = result.priceTable?.find(({ quantity }) => Number(selection?.quantity) === Number(quantity))
    return format.currency(unityPrice?.unitPrice || 0, priceCurrency, locale)
  }, [result.priceTable, selection?.quantity, priceCurrency, locale])

  const closeModalWithDelay = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsModalOpen(false)
      setIsClosing(false)
    }, 1000)
  }, [])

  const bestValue = useMemo(() => {
    if (!result.priceTable) return null
    return result.priceTable.reduce((minItem, currentItem) => {
      if (
        currentItem.unitPrice < minItem.unitPrice ||
        (currentItem.unitPrice === minItem.unitPrice && currentItem.quantity < minItem.quantity)
      ) {
        return currentItem
      }
      return minItem
    }, result.priceTable[0])
  }, [result.priceTable])

  return (
    <S.Result>
      <S.ResultWrapper>
        <S.ResultBox shadow>
          {isLoading && (
            <S.BoxShadow>
              <Loader color='inverse' size='large' />
            </S.BoxShadow>
          )}
          <Text size='xlg' family='heading' weight='bold' align='center' margin='0 0 2rem'>
            {texts.title}
          </Text>
          <S.Total>{getPriceByQuantity()}</S.Total>

          {alternativeCurrency &&
            (hasCartItems ? (
              <Tooltip title={t('CURRENCY_SWITCH_DISABLED_CART_NOT_EMPTY')}>
                <S.CurrencySwitch type='button' onClick={toggleCurrency} disabled>
                  {t('SHOW_PRICE_IN_CURRENCY', {
                    currency: priceCurrency === DEFAULT_CURRENCY ? alternativeCurrency : DEFAULT_CURRENCY
                  })}
                </S.CurrencySwitch>
              </Tooltip>
            ) : (
              <S.CurrencySwitch type='button' onClick={toggleCurrency}>
                {t('SHOW_PRICE_IN_CURRENCY', {
                  currency: priceCurrency === DEFAULT_CURRENCY ? alternativeCurrency : DEFAULT_CURRENCY
                })}
              </S.CurrencySwitch>
            ))}
          <Text color='weak' size='sm' margin='0 0 0 0'>
            {texts.unitary_price}: {getUnityPrice()}
          </Text>
          <Text color='weak' size='sm' margin='0 0 2rem' align='center'>
            {texts.unitary_price_information}
          </Text>
          {result.numberOfSheets > 0 && (
            <Text color='weak' size='sm' margin='0 0 2rem'>
              {texts.number_sheets}
            </Text>
          )}
          {compactView && (
            <ResultFooter
              texts={{
                button: texts.button,
                extra: texts.extra,
                help: texts.help,
                more_info: texts.more_info,
                footer_message: texts.footer_message
              }}
              compactView={compactView}
            />
          )}
          {!compactView && (
            <>
              <S.Table>
                <S.TableHead>
                  <S.TableCell>{texts.table.quantity}</S.TableCell>
                  <S.TableCell>{texts.table.total_price}</S.TableCell>
                  <S.TableCell>{texts.table.unit_price}</S.TableCell>
                </S.TableHead>

                {orderBy(result.priceTable, ['quantity']).map(({ quantity, totalPrice, unitPrice }) => (
                  <S.TableContents key={quantity}>
                    <S.TableRow
                      key={quantity}
                      role='button'
                      tabIndex={0}
                      highlight={Number(selection?.quantity) === Number(quantity)}
                      onClick={() => selectQuantity(quantity)}
                    >
                      <S.TableCell>{quantity}</S.TableCell>
                      <S.TableCell>{format.currency(totalPrice, priceCurrency, locale)}</S.TableCell>
                      <S.TableCell>{format.currency(unitPrice, priceCurrency, locale)}</S.TableCell>
                    </S.TableRow>
                    {Number(bestValue?.quantity) === Number(quantity) && (
                      <S.BestPriceBadge isUkShop={defaultLocale === 'uk'}>
                        <Image src={texts.best_value.url} alt={texts.best_value.alt} width='75' height='25' />
                        <S.BestValue>{texts.best_vaue_label}</S.BestValue>
                      </S.BestPriceBadge>
                    )}
                  </S.TableContents>
                ))}
              </S.Table>
              <>
                {locale && !['au', 'us'].includes(locale) && (
                  <>
                    <S.DividerLine nomargin />
                    <S.DevliveryBlockWrapper>
                      <S.DeliveryHead>
                        <span
                          className='production-type-text-block'
                          dangerouslySetInnerHTML={{ __html: texts.deliver.express_production_type_header }}
                        />
                        <span
                          className='production-type-text-block'
                          dangerouslySetInnerHTML={{ __html: texts.deliver.dispatch_date_header }}
                        />
                      </S.DeliveryHead>
                      <S.Delivery className='tour-delivery'>
                        <S.DeliveryType
                          role='button'
                          tabIndex={0}
                          selected={selection?.super_express_production === true}
                          disabled={
                            !(isDateReal(result?.dateSuperExpress) && selection.artwork_service === noArtworkServiceId)
                          }
                          onClick={() => {
                            isDateReal(result?.dateSuperExpress) && selection.artwork_service === noArtworkServiceId
                              ? selectSuperExpressDeliveryType(true)
                              : false
                          }}
                        >
                          <Icon
                            name='super-express-production'
                            className='super-express-production-icon-main delivery-type-icon-ge'
                            size='s5'
                          />
                          <div className='delivery-type-content'>
                            <span
                              className='production-type-text-block'
                              dangerouslySetInnerHTML={{ __html: texts.deliver.super_express }}
                            />
                            <Text size='sm' margin='0' weight='medium'>
                              {isDateReal(result?.dateSuperExpress)
                                ? result?.dateSuperExpress
                                : t('PRODUCTION_TYPE_NOT_POSSIBLE')}
                            </Text>
                          </div>
                          <S.IconBlock>
                            {selection?.super_express_production === true && (
                              <Icon name='checkmark-circle' className='checkmark-circle' size='s5' />
                            )}
                          </S.IconBlock>
                        </S.DeliveryType>

                        <S.DeliveryType
                          role='button'
                          tabIndex={0}
                          selected={
                            selection?.express_production === true && selection?.super_express_production === false
                          }
                          disabled={!isDateReal(result.dateExpress)}
                          onClick={() => {
                            isDateReal(result.dateExpress) ? selectDeliveryType(true) : false
                          }}
                        >
                          <Icon
                            name='express-production-48h'
                            className='express-production-48h  delivery-type-icon-ge'
                            size='s5'
                          />
                          <div className='delivery-type-content'>
                            <span
                              className='production-type-text-block'
                              dangerouslySetInnerHTML={{ __html: texts.deliver.express }}
                            />
                            <Text size='sm' margin='0' weight='medium'>
                              {isDateReal(result?.dateExpress)
                                ? result?.dateExpress
                                : t('PRODUCTION_TYPE_NOT_POSSIBLE')}
                            </Text>
                          </div>
                          <S.IconBlock>
                            {selection?.express_production === true &&
                              selection?.super_express_production === false && (
                                <Icon name='checkmark-circle' className='checkmark-circle' size='s5' />
                              )}
                          </S.IconBlock>
                        </S.DeliveryType>
                      </S.Delivery>
                      <S.DeliveryHead>
                        <span
                          className='production-type-text-block'
                          dangerouslySetInnerHTML={{ __html: texts.deliver.standard_production_type_header }}
                        />
                        <span
                          className='production-type-text-block'
                          dangerouslySetInnerHTML={{ __html: texts.deliver.estimated_dispatch_date_header }}
                        />
                      </S.DeliveryHead>
                      <S.Delivery>
                        <S.DeliveryType
                          role='button'
                          tabIndex={0}
                          selected={
                            selection.super_express_production !== undefined
                              ? selection?.express_production === false && selection?.super_express_production === false
                              : true
                          }
                          onClick={() => selectDeliveryType(false)}
                        >
                          <Icon
                            name='standard-production'
                            className='standard-icon   delivery-type-icon-ge'
                            size='s5'
                          />
                          <div className='delivery-type-content'>
                            <span
                              className='production-type-text-block'
                              dangerouslySetInnerHTML={{ __html: texts.deliver.standard }}
                            />
                            <Text size='sm' margin='0' weight='medium'>
                              {result.dateStandard}
                            </Text>
                          </div>
                          <S.IconBlock>
                            {(selection.super_express_production !== undefined
                              ? selection?.express_production === false && selection?.super_express_production === false
                              : true) && <Icon name='checkmark-circle' className='checkmark-circle' size='s5' />}
                          </S.IconBlock>
                        </S.DeliveryType>
                      </S.Delivery>
                    </S.DevliveryBlockWrapper>
                  </>
                )}
              </>
              <S.DividerLine />
              <S.QuotationMailSender role='button' tabIndex={0} onClick={() => setIsModalOpen(true)}>
                <Icon name='send' size='s3' className='qoutation-send' />
                <Text size='baseroot' color='primary' weight='medium' style={{ margin: 0 }}>
                  {texts.quotation.function_name}
                </Text>
                <Icon name='circle-arrow-right' size='s3' className='qoutation-forwards' />
              </S.QuotationMailSender>

              <S.QuotationContainer>
                {isModalOpen && (
                  <div className={`modal ${isClosing ? 'modal-hidden' : 'modal-open'}`}>
                    <Modal
                      size='medium'
                      infos={{
                        title: texts.quotation.title,
                        subtitle: texts.quotation.subtitle
                      }}
                      close={closeModalWithDelay}
                      func={handleSubmitForm}
                      isDisabled={handleValidateForm()}
                      buttonName={texts.quotation.button}
                      cancelButtonName={''}
                      className={'background'}
                      overflow={false}
                    >
                      <S.AddressForm>
                        <Input
                          isBlock
                          dimension='small'
                          type='text'
                          id='quotation_cust_name'
                          name='quotation_cust_name'
                          placeholder={texts.quotation.name}
                          value={custName}
                          onChange={(e) => {
                            setCustName(e?.currentTarget?.value)
                          }}
                        />
                        <Input
                          isBlock
                          dimension='small'
                          type='text'
                          id='quotation_cust_mail'
                          name='quotation_cust_mail'
                          placeholder={texts.quotation.email}
                          value={custEmail}
                          onChange={(e) => {
                            setCustEmail(e?.currentTarget?.value)
                          }}
                        />
                      </S.AddressForm>
                    </Modal>
                  </div>
                )}
              </S.QuotationContainer>

              <ResultFooter
                texts={{
                  button: texts.button,
                  extra: texts.extra,
                  help: texts.help,
                  more_info: texts.more_info,
                  footer_message: texts.footer_message
                }}
              />
            </>
          )}
        </S.ResultBox>
      </S.ResultWrapper>
    </S.Result>
  )
}
